import {
  useSelectedNumberByVariableKey,
  useSelectedValueByVariableKey,
  useVariableByKey,
} from '@k3/core'
import { Keys } from '../utility/utils'
import { useHighestAndLowestMax } from './useHighestMaxAndLowestMin'
import { degToRad } from 'three/src/math/MathUtils'

export const useDimensions = () => {
  const { highestMax } = useHighestAndLowestMax()

  const panelDimensions =
    (useSelectedNumberByVariableKey(Keys.PanelDimensions.Variable) ?? 156.0) /
    1000.0

  const solarCellThickness =
    (useSelectedNumberByVariableKey(Keys.SolarCellThickness.Variable) ?? 1.0) /
    1000.0

  const length =
    Math.min(
      useSelectedNumberByVariableKey(Keys.Length.Variable) ?? 0,
      highestMax
    ) / 1000.0

  const width =
    Math.min(
      useSelectedNumberByVariableKey(Keys.Width.Variable) ?? 0,
      highestMax
    ) / 1000.0

  const borderWidth =
    (useSelectedNumberByVariableKey(Keys.BorderWidth.Variable) ?? 0) / 1000.0

  const cellSpacingVertical: number =
    (useSelectedNumberByVariableKey(Keys.CellSpacingV.Variable) ?? 0) / 1000.0

  const cellSpacingHorizontal: number =
    (useSelectedNumberByVariableKey(Keys.CellSpacingH.Variable) ?? 0) / 1000.0

  const showVSG =
    useSelectedValueByVariableKey(Keys.BackGlassVSG.Variable)?.key ===
    Keys.BackGlassVSG.Value.Selected

  const thicknessVariable = useVariableByKey(Keys.Thickness.Variable)
  let thickness = useSelectedNumberByVariableKey(Keys.Thickness.Variable) ?? 0
  if (thicknessVariable) {
    thickness /= 1000.0
  } else {
    thickness = Number(
      (() => {
        const maxDimension = Math.max(length, width)
        if (showVSG) {
          return maxDimension <= 3.8
            ? 0.004
            : maxDimension > 3.5
            ? 0.008
            : 0.006
        } else {
          return maxDimension <= 2.8
            ? 0.004
            : maxDimension > 3.5
            ? 0.008
            : 0.006
        }
      })().toFixed(4)
    )
  }

  const fullThicknessVariable = useVariableByKey(Keys.FullThickness.Variable)
  let fullThickness =
    useSelectedNumberByVariableKey(Keys.FullThickness.Variable) ?? 0

  if (fullThicknessVariable) {
    fullThickness /= 1000.0
  } else {
    fullThickness = Number(
      (showVSG ? 3 * thickness + 2 * 0.002 : 2 * thickness + 0.002).toFixed(4)
    )
  }

  const backGlassOffset =
    thickness / 2 +
    (useSelectedNumberByVariableKey(Keys.BackGlassOffset.Variable) ?? 0.9) /
      1000.0

  const backGlassBlackOffset =
    (useSelectedNumberByVariableKey(Keys.BackGlassBlackOffset.Variable) ??
      1.0) / 1000.0

  const vsgBackGlassOffset =
    backGlassOffset +
    (useSelectedNumberByVariableKey(Keys.VSGBackGlassOffset.Variable) ??
      0.005) /
      1000.0 +
    thickness

  const vsgOffset =
    thickness +
    (useSelectedNumberByVariableKey(Keys.VSGOffset.Variable) ?? 0.35) / 1000.0

  return {
    angle: degToRad(12),
    length,
    width,
    borderWidth,
    thickness,
    cellSpacingVertical,
    cellSpacingHorizontal,
    fullThickness,
    panelDimensions,
    solarCellThickness,
    backGlassOffset,
    backGlassBlackOffset,
    vsgBackGlassOffset,
    vsgOffset,
  }
}
